import {
  CloseOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { HidableAvatarView } from "@/modules/common/entities/person/person-avatar";
import { Person } from "@/modules/common/entities/person";
import Card from "@/shared/data-display/card";
import Button from "@/shared/form/button";
import Text from "@/shared/typography/text";
import Tag from "@/shared/data-display/tag";
import { useDocumentsTranslations } from "@/modules/documents/shared/utils/use-documents-translations";
import { FileTypeIcon } from "@/modules/documents/shared/ui/file-type-icon";
import { DetailField } from "@/modules/documents/shared/ui/detail-field";
import {
  CompanyDocumentAnalyticsRecordFragment,
  CompanyDocumentReadConfirmationType,
} from "gql/graphql";
import { Nothing, valueOrNull } from "@/shared/value";
import { Position } from "@/modules/common/entities/company-structure/position";
import { Organization } from "@/modules/common/entities/company-structure/organization";
import { CompanyDocumentReadConfirmation } from "@/modules/documents/entities/company-document-read-confirmation";

// У неознакомившегося участника последнего действия ещё не было — на его месте
// прочерк, а не плашка «Не указано».
function Dash() {
  return <>—</>;
}

export function SelectedParticipant({
  record,
  readConfirmationType,
  onClose,
}: {
  record: CompanyDocumentAnalyticsRecordFragment;
  readConfirmationType?: CompanyDocumentReadConfirmationType;
  onClose: () => void;
}) {
  const t = useDocumentsTranslations();

  const { confirmDate, signedFile } = record;
  const person = valueOrNull(record.person);
  const reviewed = !!confirmDate || !!signedFile;
  const isSignatureDoc =
    readConfirmationType ===
    CompanyDocumentReadConfirmationType.DigitalSignature;

  return (
    <div className="grid gap-y-5">
      <Card
        title={t("Selected participant")}
        extra={
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <HidableAvatarView data={person?.avatar} size="large" />
            <div className="flex flex-col">
              <Text weight="600">
                <Person.Name data={record.person} />
              </Text>
              <Text type="caption" color="secondary">
                <Position.Name data={person?.position} whenEmpty={Nothing} />
              </Text>
              <Text type="caption" color="secondary">
                <Organization.Name
                  data={person?.organization}
                  whenEmpty={Nothing}
                />
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <DetailField label={t("Review status")}>
              {reviewed ? (
                <Text
                  color="success"
                  className="inline-flex items-center gap-1"
                >
                  {t("Reviewed")}
                  <CheckCircleOutlined />
                </Text>
              ) : (
                <Text color="secondary">{t("Not reviewed")}</Text>
              )}
            </DetailField>

            {isSignatureDoc && (
              <DetailField label={t("Digital signature status")}>
                {signedFile ? (
                  <Text
                    color="success"
                    className="inline-flex items-center gap-1"
                  >
                    {t("Signed with digital signature")}
                    <CheckCircleOutlined />
                  </Text>
                ) : (
                  <Text color="warning">{t("Awaiting signature")}</Text>
                )}
              </DetailField>
            )}

            <DetailField label={t("Last action")}>
              <CompanyDocumentReadConfirmation.ConfirmDate
                data={record}
                whenEmpty={Dash}
              />
            </DetailField>
          </div>
        </div>
      </Card>

      {signedFile && (
        <Card title={t("Signed document (digital signature)")}>
          <div className="flex items-center gap-3">
            <FileTypeIcon fileName={signedFile.fileName} className="text-2xl" />
            <div className="flex flex-col">
              <Text className="break-all">
                <CompanyDocumentReadConfirmation.SignedFileName data={record} />
              </Text>
              <Tag color="success" icon={<CheckCircleOutlined />}>
                {t("Signed with digital signature")}
              </Tag>
            </div>
            <Button
              type="text"
              icon={<DownloadOutlined />}
              href={signedFile.downloadLink}
              target="_blank"
              className="ml-auto"
            />
          </div>
        </Card>
      )}
    </div>
  );
}
